import { useBlocNext } from "@blac/react";
import type { FC } from "react";
import React, { useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import CustomSuspense from "../CustomSuspense/CustomSuspense";
import AppQueryPopupsBloc, { AppPopup } from "./AppQueryPopupsBloc";

const Async: Record<
  AppPopup,
  | React.LazyExoticComponent<FC<{ returnUrl: string }>>
  | React.LazyExoticComponent<FC<{ returnUrl?: string }>>
  | undefined
> = {
  [AppPopup.scheduleAppointment]: React.lazy(
    async () =>
      import(
        "src/ui/components/AppQueryPopups/dialogs/ScheduleAppointmentDialog"
      )
  ),
  [AppPopup.medicalInsurance]: React.lazy(
    async () =>
      import("src/ui/components/AppQueryPopups/dialogs/EnterMedicalInsurance")
  ),
  [AppPopup.appointmentBooking]: React.lazy(
    async () =>
      import(
        "src/ui/components/AppQueryPopups/dialogs/AppointmentBookingDialog"
      )
  ),
  [AppPopup.sessionConfirmation]: React.lazy(
    async () =>
      import(
        "src/ui/components/AppQueryPopups/dialogs/SessionConfirmationDialog"
      )
  ),
  [AppPopup.rpmDailyReadings]: React.lazy(
    async () => import("src/ui/components/Chat/dialogs/DailyReadingsDialog")
  ),
  [AppPopup.uploadLabReport]: React.lazy(
    async () =>
      import("src/ui/components/Chat/dialogs/ChatUploadLabResultDialog")
  ),
  [AppPopup.enterWaistCircumference]: React.lazy(
    async () =>
      import("src/ui/components/Chat/dialogs/WaistCircumferenceDialog")
  ),
  [AppPopup.enterBloodGlucose]: React.lazy(
    async () => import("src/ui/components/Chat/dialogs/BloodGlucoseDialog")
  ),
  [AppPopup.enterBloodPressure]: React.lazy(
    async () => import("src/ui/components/Chat/dialogs/BloodPressureDialog")
  ),
  [AppPopup.enterWeight]: React.lazy(
    async () => import("src/ui/components/Chat/dialogs/WeightDialog")
  ),
  [AppPopup.profileSelectPCP]: React.lazy(
    async () => import("src/ui/components/AppQueryPopups/dialogs/SelectPcp")
  ),
  [AppPopup.profileSelectPharmacy]: React.lazy(
    async () =>
      import("src/ui/components/AppQueryPopups/dialogs/SelectPharmacy")
  ),
  [AppPopup.profileEnterPharmacyInsurance]: React.lazy(
    async () =>
      import("src/ui/components/AppQueryPopups/dialogs/EnterPharmacyInsurance")
  ),
  [AppPopup.questionnaire]: React.lazy(
    async () =>
      import("src/ui/components/InAppQuestionnaire/InAppQuestionnaire")
  ),
  [AppPopup.behavioralHealth]: React.lazy(
    async () =>
      import("src/ui/components/AppQueryPopups/dialogs/BehavioralHealthDialog")
  ),
  [AppPopup.teamDetails]: React.lazy(
    async () => import("src/ui/components/Chat/dialogs/ChatCareTeamDialog")
  ),
  [AppPopup.iframe]: React.lazy(
    async () => import("src/ui/components/AppQueryPopups/dialogs/IframePopup")
  ),
  [AppPopup.scheduling]: React.lazy(
    async () => import("src/ui/components/AppQueryPopups/dialogs/IframePopup")
  ),
  [AppPopup.article]: React.lazy(
    async () => import("src/ui/components/AppQueryPopups/dialogs/IframePopup")
  ),
  [AppPopup.appointment]: React.lazy(
    async () =>
      import("src/ui/components/AppQueryPopups/dialogs/AppointmentDialog")
  ),
  [AppPopup.rescheduleAppointment]: React.lazy(
    async () =>
      import(
        "src/ui/components/AppQueryPopups/dialogs/RescheduleAppointmentDialog"
      )
  ),
  [AppPopup.rxDietNutritionProgram]: React.lazy(
    async () =>
      import(
        "src/ui/components/AppQueryPopups/dialogs/RxDietNutritionProgramDialog"
      )
  ),
  [AppPopup.chooseYourNextJourney]: React.lazy(
    async () =>
      import(
        "src/ui/components/AppQueryPopups/dialogs/ChooseYourNextJourneyDialog"
      )
  ),
  [AppPopup.healthSyncSetup]: React.lazy(
    async () =>
      import("src/ui/components/AppQueryPopups/dialogs/HealthSyncSetupDialog")
  ),
  [AppPopup.lifestyleProgram]: React.lazy(
    async () =>
      import("src/ui/components/AppQueryPopups/dialogs/LifestyleProgramDialog")
  ),
  [AppPopup.initialLab]: React.lazy(
    async () =>
      import(
        "src/ui/components/AppQueryPopups/dialogs/InitialLabDialog/InitialLabDialog"
      )
  ),
  [AppPopup.labInstructions]: React.lazy(
    async () =>
      import("src/ui/components/AppQueryPopups/dialogs/LabInstructionsDialog")
  ),
  [AppPopup.sanityContent]: React.lazy(
    async () => import("src/ui/components/AppQueryPopups/dialogs/SanityDialog")
  ),
  [AppPopup.onboardingTasks]: React.lazy(
    async () =>
      import("src/ui/components/AppQueryPopups/dialogs/OnboardingTasksPopup")
  ),
  [AppPopup.glpContinuation]: React.lazy(
    async () =>
      import("src/ui/components/AppQueryPopups/dialogs/GlpContinuationPopup")
  ),
  [AppPopup.fastPass]: React.lazy(
    async () => import("src/ui/components/AppQueryPopups/dialogs/FastPassPopup")
  ),
  [AppPopup.glp1Refill]: React.lazy(
    async () =>
      import("src/ui/components/AppQueryPopups/dialogs/GLP1RefillDialog")
  ),
  [AppPopup.glp1StartDate]: React.lazy(
    async () =>
      import("src/ui/components/AppQueryPopups/dialogs/GLP1StartDateDialog")
  ),
  [AppPopup.faq]: React.lazy(
    async () => import("src/ui/components/AppQueryPopups/dialogs/FaqDialog")
  ),
  [AppPopup.rateCoachingCall]: React.lazy(
    async () =>
      import("src/ui/components/AppQueryPopups/dialogs/CoachRatingDialog")
  ),
  [AppPopup.prescriptionLabelUpload]: React.lazy(
    async () =>
      import(
        "src/ui/components/AppQueryPopups/dialogs/PrescriptionLabelUploadDialog"
      )
  ),
  [AppPopup.weightProofUpload]: React.lazy(
    async () =>
      import("src/ui/components/AppQueryPopups/dialogs/WeightProofUploadDialog")
  )
};

const getPopupComponent = (popup: string) => {
  const lowercasePopup = popup.toLowerCase();
  const keys = Object.keys(Async);
  for (const key of keys) {
    if (key.toLowerCase() === lowercasePopup) {
      return Async[key as AppPopup];
    }
  }
};

const AppQueryPopups = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [query] = useSearchParams();

  const [{ popup }, { setCurrentQuery, setNavigate }] =
    useBlocNext(AppQueryPopupsBloc);
  setNavigate(navigate);

  useEffect(() => {
    setCurrentQuery(query);
  }, [query.toString()]);

  const Component = getPopupComponent(popup ?? "");

  return (
    <CustomSuspense>
      {Component && <Component key={popup} returnUrl={pathname} />}
    </CustomSuspense>
  );
};

export default AppQueryPopups;
